import { search } from "algorithms/search";

import { Button } from "components/common/button";
import { Division } from "components/common/division";
import { Form } from "components/common/form";
import { Input } from "components/common/input";

import { Empty } from "components/empty";
import { Result } from "components/result";

import { getElementById, getStaticProps, isEmpty } from "utils";

import { Page } from "types";

/**
 * Repräsentiert eine Suche mit grafischer Benutzeroberfläche.
 *
 * @remarks
 * Es wird die unscharfe Suche unterstützt. Rechtschreibfehler im Suchbegriff
 * sind deshalb unerheblich.
 *
 * @example
 * Das folgende Beispiel erweitert eine Seite um eine Suche.
 *
 * Erstelle zuerst die erforderlichen HTML Elemente.
 *
 * ```html
 * <html>
 *
 *  <body>
 *    <!-- Dient der Darstellung der Suche. -->
 *    <div id="search"></div>
 *
 *    <!-- Dient der Darstellung der Suchergebnisse. -->
 *    <div id="results"></div>
 *
 *    <!-- Enthält den Suchindex. -->
 *    <script id="__STATIC_PROPS__" type="application/json">
 *      [
 *      	{
 *          "title": "Lorem Ipsum",
 *          "path": "/lorem-ipsum",
 *          "date": "2022-01-01"
 *        },
 *        {
 *          "title": "Dolor Sit Amet",
 *          "path": "/dolor-sit-amet",
 *          "date": "2022-12-24"
 *        }
 *      ]
 *    </script>
 *
 *    <!-- Enthält das JavaScript-Bündel. -->
 *    <script src="/dist/bundle.js"></script>
 *  </body>
 *
 * </html>
 * ```
 *
 * Erstelle dann eine neue Suche.
 *
 * ```typescript
 * import { Search } from "components/search";
 *
 * const search = new Search();
 * ```
 *
 * Rendere nun die Suche in das DOM.
 *
 * ```typescript
 * document.getElementById("search")
 *     ?.replaceChildren(search.render());
 * ```
 *
 * @public
 */
class Search extends Division {
  /**
   * Das HTML `<form>` Element.
   */
  private form: Form;

  /**
   * Das HTML `<div>` Element.
   */
  private group: Division;

  /**
   * Das HTML `<input>` Element.
   */
  private input: Input;

  /**
   * Das HTML `<button type="reset">` Element.
   */
  private reset: Button;

  /**
   * Das HTML `<button type="submit">` Element.
   */
  private submit: Button;

  /**
   * Der Suchindex.
   */
  private index: Array<Page>;

  /**
   * Das HTML `<div>` Element für die Suchergebnisse.
   */
  private results: HTMLDivElement;

  /**
   * Erstellt eine neue Suche.
   */
  constructor() {
    super();

    /* Deserialisiere den Suchindex. */
    this.index = getStaticProps<Array<Page>>();

    /* Erhalte das HTML `<div>` Element für die Suchergebnisse. */
    this.results = getElementById("results");

    /* Erzeuge die erforderlichen HTML Elemente. */
    this.form = new Form();
    this.group = new Division();
    this.input = new Input();
    this.reset = new Button();
    this.submit = new Button();

    /* Füge die CSS Klassen hinzu. */
    this.withClass("search");

    this.form.withClass("form");
    this.group.withClass("group");
    this.input.withClass("input");
    this.reset.withClass("reset");
    this.submit.withClass("submit");

    /* Füge die Typen hinzu. */
    this.input.withType("search");
    this.reset.withType("reset");
    this.submit.withType("submit");

    /* Füge die Beschriftungen hinzu. */
    this.reset.withText("\u00D7"); // Unicode: x
    this.submit.withText("Suche");

    /* Füge den Platzhalter hinzu. */
    this.input.withPlaceholder("Begriff suchen");

    /* Deaktiviere die Autovervollständigung. */
    this.input.withAutocomplete("off");

    /* Verhindere zu Beginn das Zurücksetzen. */
    this.reset.hide();

    /* Konfiguriere die Event Listener. */
    this.hydrate();
  }

  /**
   * Konfiguriert die Event Listener.
   */
  private hydrate() {
    const { form, input, reset } = this;

    /* Zeige zu Beginn die vollständige Liste der Begriffe an. */
    window.addEventListener("load", () => this.handleInitial());

    /* Fokussiere zu Beginn das Eingabefeld. */
    window.addEventListener("load", () => input.focus());

    /* Verhindere die serverseitige Übertragung. */
    form.withEventListener("submit", (event) => event.preventDefault());

    /* Durchsuche den Suchindex mit der unscharfen Suche. */
    form.withEventListener("submit", () => this.handleSearch());

    /* Zeige wieder die vollständige Liste der Begriffe an. */
    form.withEventListener("reset", () => this.handleInitial());

    /* Ermögliche das Zurücksetzen, wenn eine Eingabe vorhanden ist. */
    input.withEventListener("keyup", () => {
      input.getValue()
        ? // Ermögliche das Zurücksetzen.
          reset.show()
        : // Verhindere das Zurücksetzen.
          reset.hide();
    });

    /* Verhindere das Zurücksetzen, nachdem zurückgesetzt wurde. */
    reset.withEventListener("click", () => reset.hide());

    /* Fokussiere das Eingabefeld, nachdem dieses zurückgesetzt wurde. */
    reset.withEventListener("click", () => input.focus());
  }

  /**
   * Zeigt die vollständige Liste aller Begriffe an.
   */
  private handleInitial() {
    this.results.replaceChildren(
      ...this.index.map((page) => new Result(page).render())
    );
  }

  /**
   * Durchsucht den Suchindex mit der unscharfen Suche.
   *
   * @remarks
   * Werden Suchergebnisse gefunden, werden diese angezeigt.
   *
   * Werden keine Suchergebnisse gefunden, wird ein entsprechender Hinweis
   * angezeigt.
   */
  private handleSearch() {
    /* Erhalte den aktuellen Suchbegriff. */
    const query = this.input.getValue();

    /* Durchsuche den Suchindex mit der unscharfen Suche. */
    const results = search(query, this.index);

    /* Es wurden keine Suchergebnisse gefunden. */
    if (isEmpty(results)) {
      this.results.replaceChildren(new Empty("Keine Suchergebnisse").render());
    }

    /* Zeige die Liste der Suchergebnisse an. */
    if (!isEmpty(results)) {
      this.results.replaceChildren(
        ...results.map(({ page }) => new Result(page).render())
      );
    }
  }

  /**
   * Gibt die Suche zurück.
   *
   * @returns Die Suche.
   * @override
   * @public
   */
  public render() {
    const { form, group, input, reset, submit } = this;

    /* Erstelle das HTML Markup. */
    this.withChildren(
      form.withChildren(group.withChildren(input, reset), submit)
    );

    return this.element;
  }
}

export { Search };
